import { useEffect } from 'react';
import {
  matchRoutes,
  useLocation,
  useNavigationType,
  createBrowserRouter,
  RouterProvider,
  createRoutesFromChildren,
} from 'react-router-dom';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from '@sentry/react';
// routes
import routes from './routes/routes';
// Store
import { AppProvider } from './store/AppContext';

// Sentry init
import { isInSentryExceptionsAllowList } from './services/sentryAllowList';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    // See docs for support of different versions of variation of react router
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],

  /**
   * @param {Sentry.ErrorEvent} event
   * @param {object} hint
   * @param {import('axios').AxiosError | Error} hint.originalException
   * @returns {Sentry.ErrorEvent | null}
   */
  beforeSend(event, hint) {
    const error = hint.originalException;
    if (error instanceof AxiosError) {
      if (isInSentryExceptionsAllowList(error)) {
        return null;
      }
      return {
        ...event,
        extra: {
          ...event.extra,
          error: error.response.data,
        },
      };
    }
    return event;
  },

  enabled: !import.meta.env.DEV,
  environment: import.meta.env.MODE,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  tracesSampleRate: 1.0,

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Create router wrapped in Sentry
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(
  createBrowserRouter,
);
const router = sentryCreateBrowserRouter(routes);

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
      refetchOnWindowFocus: false,
    },
  },

  // // Example code for handling global toast errors with React Query
  // // Uncomment the code below to enable it

  // mutationCache: new MutationCache({
  //   onError: (error, _, __, mutation) => {
  //     if (!mutation?.options?.meta?.preventToastError) {
  //       console.log('ERROR', error);
  //       // Handle error and show toast
  //     }
  //   },
  // }),

  // queryCache: new QueryCache({
  //   onError: (error, query) => {
  //     if (!query?.options?.meta?.preventToastError) {
  //       // Handle error and show toast
  //     }
  //   },
  // }),
});

/**
 * The main component of the application.
 * @returns {JSX.Element} The App component
 */
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <RouterProvider router={router} />
      </AppProvider>

      <ReactQueryDevtools />
    </QueryClientProvider>
  );
}

export default App;
